import React, { useMemo, useState, useEffect } from "react";
import { TrekMenubar } from "components/layout/MenuBar";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Nav from "components/layout/Nav";
import { featchTrek, addTrek, softDeleteTrek, fetchTrashedTrek } from "../../../Slices/trek.slice";
import { DataTable } from "components/shared/table";
import { CreateLocation } from "components/shared/buttons";
import { IoIosCloseCircle } from "react-icons/io";
import { Overview } from "components/forms/overview";
import { canAccessSegment, filterByLocationAccess } from "utils/accessControl";
import { showToast } from "components/shared/Toast";
import { useGetEditorsQuery } from "Slices/rollManager/subAdmin.slice";
import { buildEditDetailsPath } from "utils/locationSlug";


const TrekCMS = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { status, error, trek } = useSelector((state) => state.trek);
    const { user } = useSelector((state) => state.auth);
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const isSuperAdmin = user?.role === "superAdmin" || user?.isSuperAdmin;
    const isDistrictManager = user?.role === "districtManager";
    const isEditor = user?.role === "editor";
    const isManager = user?.role === "districtManager" || user?.role === "subAdmin";
    const { data: managedEditors = [] } = useGetEditorsQuery(user?.id, {
        skip: !isManager || !user?.id,
    });

    // ✅ Fetch trek data initially
    useEffect(() => {
        if (!trek.length) {
            dispatch(featchTrek());
        }
    }, [dispatch, trek.length]);

    const handleAddTrekClick = () => setShowForm(true);
    const closePopup = () => setShowForm(false);

    const handleFormSubmit = (formData) => {
        dispatch(addTrek(formData))
            .then((result) => {
                if (result.type.endsWith("/fulfilled")) {
                    showToast("Trek saved and sent for approval.", "success");
                    setShowForm(false);
                    dispatch(featchTrek());
                } else {
                    showToast("Failed to add trek. Please try again.", "error");
                }
            })
            .catch((error) => {
                console.error("Error adding trek:", error);
                showToast("Failed to add trek. Please try again.", "error");
            });
    };

    const handleDelete = (id) => {
        dispatch(softDeleteTrek(id)).then((result) => {
            if (result.type.endsWith("/fulfilled")) {
                dispatch(featchTrek());
                dispatch(fetchTrashedTrek());
            }
        });
    };

    const handleEditClick = (location) => {
        navigate(buildEditDetailsPath("trek", location, "Overview"));
    };

    // ✅ Filter locations based on search term
    const managedEditorIds = useMemo(
        () => new Set((managedEditors || []).map((editor) => editor.id)),
        [managedEditors]
    );
    const locationScoped = useMemo(() => filterByLocationAccess(trek, user), [trek, user]);
    const locationScopedIds = useMemo(
        () => new Set((locationScoped || []).map((loc) => loc._id)),
        [locationScoped]
    );
    const accessibleTrek = useMemo(() => {
        if (isSuperAdmin) return trek;
        if (isEditor) return trek;
        if (!isManager) return locationScoped;
        return trek.filter(
            (loc) =>
                locationScopedIds.has(loc._id) ||
                managedEditorIds.has(loc.createdById) ||
                loc.createdById === user?.id
        );
    }, [trek, isSuperAdmin, isEditor, isManager, locationScoped, locationScopedIds, managedEditorIds, user?.id]);

    const editorNameById = useMemo(() => {
        const map = new Map();
        (managedEditors || []).forEach((editor) => {
            if (editor?.id) {
                map.set(editor.id, editor.name || editor.id);
            }
        });
        return map;
    }, [managedEditors]);

    const filteredLocations = accessibleTrek
        .filter((loc) => loc.isDeleted === false)
        .filter((loc) =>
            [loc.locationName, loc.type].some((field) =>
                field?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        .filter((loc) => {
            if (user?.role === "editor") return loc.createdById === user?.id;
            return true;
        })
        .map((loc) => ({
            ...loc,
            locationName: loc.locationName || loc.title || "Unknown Name",
            type: loc.type || "Unknown Type",
            updatedAt: loc.updatedAt || new Date().toISOString(),
            createdByLabel:
                loc.createdByRole === "editor"
                    ? (editorNameById.get(loc.createdById) || loc.createdById || "Editor")
                    : loc.createdById || "—",
        }));
    const canManageTrek = canAccessSegment(user, "Trek");

    return (
        <>
            <Nav />
            <div className="row">
                <TrekMenubar setSearchTerm={setSearchTerm} />
                <main className="adminPageMain adminPageMain--slate">
                    <div className="adminPageShell">
                    {status === "loading" && <p>Loading locations...</p>}
                    {error && <p style={{ color: "red" }}>Error: {error}</p>}
                    {!canManageTrek && (
                        <div style={{ padding: "2rem" }}>
                            <h4 style={{ color: "#ff5151" }}>Access restricted</h4>
                            <p>You do not have permission to manage the Trek segment.</p>
                        </div>
                    )}

                    {canManageTrek && location.pathname === "/cms/trek/all-locations" && (
                        <div className="locationsTableView">
                            <div className="row justify-content-between align-items-center locationsTableToolbar">
                                <CreateLocation buttonText={"Add Trek"} showForm={showForm} setShowForm={handleAddTrekClick} />
                            </div>

                            {showForm && (
                                <div className="popup overviewPopup">
                                    <div className="row" style={{ columnGap: "30px", justifyContent: "space-between" }}>
                                        <i className="warning">
                                            For creating a location, please fill the location's overview details
                                        </i>
                                        <IoIosCloseCircle className="close" onClick={closePopup} />
                                    </div>
                                    <Overview
                                        location={""}
                                        onSubmit={handleFormSubmit}
                                        allowedSegments={user?.access?.segments || "trek"}
                                        contentType="trek"
                                    />
                                </div>
                            )}

                            <DataTable
                                locations={filteredLocations}
                                editClick={handleEditClick}
                                showCategoery={true}
                                showStatus
                                showCreatedBy
                                highlightRecent={isSuperAdmin || isDistrictManager}
                                deleteConfirmation={handleDelete}
                            />
                        </div>
                    )}
                    </div>
                </main>
            </div>
        </>
    );
};

export default TrekCMS;
